import type { EmittedWorkflow } from './emit.js';
import type {
  LiveEnvironmentConstraints,
  LiveWorkflow,
  WorkflowFieldChanges,
} from './diff-types.js';

export type WorkflowFieldDiff = {
  workflowFieldChanges: WorkflowFieldChanges;
  nameDrift: { from: string; to: string } | null;
};

export function diffWorkflowFields(spec: EmittedWorkflow, live: LiveWorkflow): WorkflowFieldDiff {
  const workflowFieldChanges: WorkflowFieldChanges = {};
  if (spec.description !== live.description) workflowFieldChanges.description = spec.description;
  if (spec.preflight !== live.preflight) workflowFieldChanges.preflight = spec.preflight;
  if (spec.cleanup !== live.cleanup) workflowFieldChanges.cleanup = spec.cleanup;

  // Status / flowType: only push when the spec sets a value AND it differs.
  // Live `flowType` defaults to 'standard' when the spec omits it -- don't churn.
  if (spec.status !== undefined && spec.status !== live.status) {
    workflowFieldChanges.status = spec.status;
  }
  // flowType: tracking only the explicit change. If spec omits flowType AND
  // live row is non-standard (e.g. seed), don't churn -- that's an authoring
  // ambiguity, not a drift.
  if (spec.flowType !== undefined && spec.flowType !== live.flowType) {
    workflowFieldChanges.flowType = spec.flowType;
  }

  // apiOnly: spec omitting it = false. Only diff when live differs.
  const specApiOnly = spec.apiOnly === true;
  const liveApiOnly = live.apiOnly === true;
  if (specApiOnly !== liveApiOnly) workflowFieldChanges.apiOnly = specApiOnly;

  // Tags: compare sorted name-sets. Spec omitting `tags` = no constraint
  // (don't strip live tags); empty array = explicit "no tags" (strip).
  if (spec.tags !== undefined) {
    const specSet = [...spec.tags].sort();
    const liveSet = (live.tags ?? []).map((t) => t.name).sort();
    if (specSet.length !== liveSet.length || specSet.some((v, i) => v !== liveSet[i])) {
      workflowFieldChanges.tags = spec.tags;
    }
  }

  // Environment constraints: compare in name-form (the form the spec authors).
  // Spec omitting = no change; spec setting = full replacement.
  if (spec.environmentConstraints !== undefined) {
    if (!sameEnvConstraints(spec.environmentConstraints, live.environmentConstraints)) {
      workflowFieldChanges.environmentConstraints = spec.environmentConstraints;
    }
  }

  // Sandbox template: compare by name (live resolves id->name on GET).
  if (spec.sandboxTemplate !== undefined) {
    const liveName = live.sandboxTemplate?.name ?? null;
    if (spec.sandboxTemplate !== liveName) {
      workflowFieldChanges.sandboxTemplate = spec.sandboxTemplate;
    }
  }

  // Teardown linkage: only meaningful for seed workflows; for non-seeds the
  // API clears these on flowType change. Compare each axis independently -- a
  // snapshot pin or disable shouldn't be conflated with relinking.
  if (spec.teardownSpec !== undefined) {
    const liveName = live.teardownWorkflowName ?? null;
    if (spec.teardownSpec !== liveName) workflowFieldChanges.teardownSpec = spec.teardownSpec;
  }
  if (spec.teardownSnapshotVersion !== undefined) {
    // Normalize: 'latest' equals the DB form `null` (always use latest).
    const specVal: number | null =
      spec.teardownSnapshotVersion === 'latest' ? null : spec.teardownSnapshotVersion;
    const liveVal = live.teardownSnapshotVersion ?? null;
    if (specVal !== liveVal) {
      workflowFieldChanges.teardownSnapshotVersion = spec.teardownSnapshotVersion;
    }
  }
  if (spec.teardownEnabled !== undefined) {
    const liveVal = live.teardownEnabled === true;
    if (spec.teardownEnabled !== liveVal) {
      workflowFieldChanges.teardownEnabled = spec.teardownEnabled;
    }
  }

  // Seed declared outputs: compare on the authored slice (dataKey, label,
  // description, propertyName). Merge live `credentialOutput`/`attributes` per
  // dataKey on the merged entries so sync.ts re-sends them intact (the spec
  // doesn't author them yet; clobbering would lose UI-authored configs).
  if (spec.seedOutputs !== undefined) {
    const liveByKey = new Map((live.seedOutputs ?? []).map((o) => [o.dataKey, o] as const));
    const sliced = (live.seedOutputs ?? []).map((o) => ({
      dataKey: o.dataKey,
      label: o.displayLabel,
      description: o.description,
      propertyName: o.propertyName,
    }));
    const specSliced = spec.seedOutputs.map((o) => ({
      dataKey: o.dataKey,
      label: o.label,
      description: o.description,
      propertyName: o.propertyName,
    }));
    if (!seedOutputsEqual(sliced, specSliced)) {
      // Reattach preserved fields per dataKey for the wire payload.
      workflowFieldChanges.seedOutputs = spec.seedOutputs.map((o) => {
        const liveRow = liveByKey.get(o.dataKey);
        return {
          dataKey: o.dataKey,
          label: o.label,
          description: o.description,
          propertyName: o.propertyName,
          ...(liveRow?.credentialOutput !== undefined
            ? { credentialOutput: liveRow.credentialOutput }
            : {}),
          ...(liveRow?.attributes !== undefined ? { attributes: liveRow.attributes } : {}),
        };
      });
    }
  }

  // Inputs/scenarios: compare only the authorable slice. The live workflow
  // carries richer definitions (input label/type/required, scenario UUIDs)
  // that the `.canary.md` grammar can't express; `emit` reproduces only
  // name/defaultValue (inputs) and name/values/credentials (scenarios). A raw
  // deep-compare would flag these as changed on every pull and overwrite the
  // rich fields with the lossy spec form. Only flag -- and push -- when the
  // authored content actually differs.
  if (!deepEquals(authoredInputs(live.inputsJson), authoredInputs(spec.inputsJson))) {
    workflowFieldChanges.inputsJson = spec.inputsJson;
  }
  if (!deepEquals(authoredScenarios(live.scenariosJson), authoredScenarios(spec.scenariosJson))) {
    workflowFieldChanges.scenariosJson = spec.scenariosJson;
  }

  const nameDrift = spec.name !== live.name ? { from: live.name, to: spec.name } : null;
  if (nameDrift) workflowFieldChanges.name = spec.name;

  return { workflowFieldChanges, nameDrift };
}

/**
 * Authorable slice of `inputsJson` -- the (kind, name, defaultValue) triples a
 * `.canary.md` `## Inputs` list can express. Drops label/type/required and
 * normalizes a missing default to '' so live and round-tripped forms match.
 */
function authoredInputs(raw: unknown): Array<{ kind: string; name: string; defaultValue: string }> {
  if (!Array.isArray(raw)) return [];
  const out: Array<{ kind: string; name: string; defaultValue: string }> = [];
  for (const i of raw) {
    if (!i || typeof i !== 'object') continue;
    const rec = i as Record<string, unknown>;
    if (typeof rec.name !== 'string') continue;
    out.push({
      kind: rec.kind === 'credential' ? 'credential' : 'value',
      name: rec.name,
      defaultValue: typeof rec.defaultValue === 'string' ? rec.defaultValue : '',
    });
  }
  return out;
}

/**
 * Authorable slice of `scenariosJson` -- name, description, default/skip flags,
 * and value/credential overrides. Drops the per-scenario UUID (`emit`
 * regenerates it each compile) so an unedited pull doesn't churn scenarios.
 */
function authoredScenarios(raw: unknown): Array<Record<string, unknown>> {
  if (!Array.isArray(raw)) return [];
  const out: Array<Record<string, unknown>> = [];
  for (const s of raw) {
    if (!s || typeof s !== 'object') continue;
    const rec = s as Record<string, unknown>;
    if (typeof rec.name !== 'string') continue;
    out.push({
      name: rec.name,
      description: typeof rec.description === 'string' ? rec.description : '',
      isDefault: rec.isDefault === true,
      includeInSuite: rec.includeInSuite !== false,
      values: strRecord(rec.values),
      credentials: strRecord(rec.credentials),
    });
  }
  return out;
}

/**
 * True when spec and live environment constraints describe the same set. The
 * spec carries names (which the author writes); the live workflow carries ids
 * plus, when the API resolved them, names. Compare on the field the spec
 * sets -- environment names for `only`, types for `envTypes`.
 */
function sameEnvConstraints(
  spec: WorkflowFieldChanges['environmentConstraints'],
  live: LiveEnvironmentConstraints | null | undefined
): boolean {
  if (spec === null || spec === undefined) return !live;
  if (!live) return false;
  if (spec.mode !== live.mode) return false;
  if (spec.mode === 'only' && live.mode === 'only') {
    const a = [...spec.environmentNames].sort();
    const b = [...(live.environmentNames ?? [])].sort();
    return a.length === b.length && a.every((v, i) => v === b[i]);
  }
  if (spec.mode === 'envTypes' && live.mode === 'envTypes') {
    const a = [...spec.types].sort();
    const b = [...live.types].sort();
    return a.length === b.length && a.every((v, i) => v === b[i]);
  }
  return false;
}

/**
 * Order-insensitive compare on the authored slice of seed outputs. Two arrays
 * match when they cover the same set of dataKeys and every per-key (label,
 * description, propertyName) triple agrees.
 */
function seedOutputsEqual(
  a: Array<{ dataKey: string; label?: string; description?: string; propertyName?: string }>,
  b: Array<{ dataKey: string; label?: string; description?: string; propertyName?: string }>
): boolean {
  if (a.length !== b.length) return false;
  const sortByKey = (rows: typeof a) =>
    [...rows].sort((x, y) => x.dataKey.localeCompare(y.dataKey));
  const xa = sortByKey(a);
  const xb = sortByKey(b);
  for (let i = 0; i < xa.length; i++) {
    if (xa[i].dataKey !== xb[i].dataKey) return false;
    if ((xa[i].label ?? '') !== (xb[i].label ?? '')) return false;
    if ((xa[i].description ?? '') !== (xb[i].description ?? '')) return false;
    if ((xa[i].propertyName ?? '') !== (xb[i].propertyName ?? '')) return false;
  }
  return true;
}

function strRecord(v: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (v && typeof v === 'object') {
    for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
      if (typeof val === 'string') out[k] = val;
    }
  }
  return out;
}

function deepEquals(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null) return a === b;
  if (typeof a !== typeof b) return false;
  if (typeof a !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!deepEquals(a[i], b[i])) return false;
    }
    return true;
  }
  const ao = a as Record<string, unknown>;
  const bo = b as Record<string, unknown>;
  const ak = Object.keys(ao);
  const bk = Object.keys(bo);
  if (ak.length !== bk.length) return false;
  for (const k of ak) {
    if (!Object.prototype.hasOwnProperty.call(bo, k)) return false;
    if (!deepEquals(ao[k], bo[k])) return false;
  }
  return true;
}
